import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

/** BroadcastChannel name carrying the cross-tab logout signal. */
const LOGOUT_CHANNEL = 'qlm-oidc-translator-logout';
/** Web Locks name serializing refresh-token redemption across tabs. */
const REFRESH_LOCK = 'qlm-oidc-translator-refresh';

/**
 * Thin facade over angular-oauth2-oidc's OAuthService for the QLM Authorization Code +
 * PKCE flow. This is the app's only auth path.
 *
 * Endpoints are configured explicitly (see environment.ts) rather than via the discovery
 * document, so `loadDiscoveryDocument` is intentionally never called.
 */
@Injectable({ providedIn: 'root' })
export class OidcAuthService {
  private logoutChannel?: BroadcastChannel;
  private configured = false;

  // Emits (with the URL the tab should navigate to) when ANOTHER tab broadcasts a logout.
  // LoginService subscribes to mirror the logout locally without re-broadcasting.
  private readonly _remoteLogout$ = new Subject<string>();
  readonly remoteLogout$: Observable<string> = this._remoteLogout$.asObservable();

  constructor(private readonly oauth: OAuthService) {}

  /** Wire OAuthService from environment.oidc. */
  configure(): void {
    // Idempotent: a second call would double-wrap refreshToken with the same lock name
    // (self-deadlock) and open a duplicate logout channel. Only ever wire up once.
    if (this.configured) {
      return;
    }
    this.configured = true;
    const cfg = environment.oidc;
    const authConfig: AuthConfig = {
      issuer: cfg.issuer,
      loginUrl: cfg.loginUrl,
      tokenEndpoint: cfg.tokenEndpoint,
      redirectUri: cfg.redirectUri,
      clientId: cfg.clientId,
      responseType: 'code',
      scope: cfg.scope,
      // Public SPA client: PKCE (on by default for the code flow) proves the redeemer,
      // there is no client secret. Endpoints are set explicitly, so discovery validation
      // is off.
      strictDiscoveryDocumentValidation: false,
      // Only enforce HTTPS in built environments; dev runs http://localhost.
      requireHttps: environment.production,
      clearHashAfterLogin: true,
    };
    this.oauth.configure(authConfig);
    // Serialize refresh-token redemption across tabs BEFORE the automatic timer is armed,
    // so the wrapper is in place for the very first refresh.
    this.serializeRefreshAcrossTabs();
    this.listenForRemoteLogout();
    // Renew the access token in the background before it expires (by default at 75% of
    // its lifetime) via the rotating refresh token -- in code flow this is a plain XHR
    // to the token endpoint, no redirect/iframe, so a long editing session never hits a
    // mid-session expiry and never loses in-page state. If a refresh ever fails, the
    // token simply expires and the existing reactive path (401 -> silent re-auth via
    // the AS session) takes over.
    this.oauth.setupAutomaticSilentRefresh();
  }

  /**
   * Serialize every refresh-token redemption across all tabs of this origin via the Web
   * Locks API. With tokens in localStorage (shared across tabs), each tab's automatic
   * silent-refresh timer could otherwise fire at once and present the SAME rotating refresh
   * token -> the AS's reuse detection revokes the whole grant and logs the user out
   * everywhere. Wrapping OAuthService.refreshToken -- the single method both the automatic
   * timer (refreshInternal) and the reactive 401 path call -- makes only one tab redeem at a
   * time; the next tab, once it holds the lock, reads the freshly rotated refresh_token from
   * localStorage (refreshToken() re-reads it at call time) so no stale token is replayed.
   * No-op where Web Locks is unavailable (older browsers / insecure context), which simply
   * preserves the previous unserialized behavior.
   */
  private serializeRefreshAcrossTabs(): void {
    const locks = globalThis.navigator?.locks;
    if (!locks) {
      return;
    }
    const original = this.oauth.refreshToken.bind(this.oauth);
    this.oauth.refreshToken = () => locks.request(REFRESH_LOCK, () => original());
  }

  /** Subscribe to logout broadcasts from other tabs and surface them via remoteLogout$. */
  private listenForRemoteLogout(): void {
    if (typeof BroadcastChannel === 'undefined') {
      return;
    }
    this.logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL);
    this.logoutChannel.onmessage = (ev: MessageEvent<string>) =>
      this._remoteLogout$.next(ev.data);
  }

  /**
   * Tell every other open tab to drop its session too and navigate to `targetUrl`. With
   * tokens in shared localStorage a logout in one tab removes them for all, but the others
   * keep showing a logged-in UI on their stale in-memory tokens until their next API call
   * fails -- this makes them react immediately, and sends them to the SAME place this tab is
   * going (translator /login for a forced logout, the main app for a deliberate sign-out) so
   * all tabs stay consistent. BroadcastChannel never echoes to the sender.
   */
  broadcastLogout(targetUrl: string): void {
    this.logoutChannel?.postMessage(targetUrl);
  }

  /**
   * Start the redirect to QLM. `targetUrl` is restored after the round-trip. Pass
   * `{ prompt: 'none' }` for a silent attempt: the AS issues a code without any UI if a
   * session already exists, or redirects back with `error=login_required` if it doesn't.
   */
  startLogin(targetUrl?: string, options?: { prompt?: 'none' }): void {
    const params = options?.prompt ? { prompt: options.prompt } : {};
    this.oauth.initCodeFlow(targetUrl ?? '', params);
  }

  /**
   * Process the redirect back from QLM (called on the /auth/callback route). Resolves to
   * true when a valid session was established.
   */
  async completeLogin(): Promise<boolean> {
    await this.oauth.tryLogin();
    return this.oauth.hasValidAccessToken();
  }

  /**
   * Force a token refresh via the rotating refresh token (code-flow XHR, no redirect) and
   * resolve true when a valid access token is in place afterwards. Used by the 401
   * interceptor to recover an expired token and retry the failed request before falling
   * back to re-auth (e.g. an autosave firing after the device slept past the token TTL).
   *
   * Concurrent callers share one in-flight refresh: with rotateRefreshToken + reuse
   * detection on the provider, two refreshes presenting the same refresh token would get
   * the whole grant revoked, so several requests 401ing at once must not each refresh.
   */
  refreshToken(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.doRefresh().finally(() => {
        this.refreshInFlight = undefined;
      });
    }
    return this.refreshInFlight;
  }

  private refreshInFlight?: Promise<boolean>;

  private async doRefresh(): Promise<boolean> {
    try {
      await this.oauth.refreshToken();
      return this.oauth.hasValidAccessToken();
    } catch {
      return false;
    }
  }

  /** The `targetUrl` passed to startLogin, restored after tryLogin. */
  get postLoginTarget(): string | undefined {
    return this.oauth.state ? decodeURIComponent(this.oauth.state) : undefined;
  }

  get isLoggedIn(): boolean {
    return this.oauth.hasValidAccessToken();
  }

  get accessToken(): string {
    return this.oauth.getAccessToken();
  }

  /** Scopes actually granted by QLM, falling back to the requested ones. */
  get grantedScopes(): string[] {
    const granted = this.oauth.getGrantedScopes() as string[] | null;
    if (granted?.length) {
      return granted;
    }
    return environment.oidc?.scope?.split(' ') ?? [];
  }

  /**
   * Clear ONLY the translator's in-memory OIDC tokens. Leaves the QLM (AS) session intact
   * (deliberately does NOT hit the AS `end_session` endpoint — that would be a global
   * sign-out) and does NOT navigate. Callers decide where to send the browser afterwards
   * (local logout → main app; a failed/expired session → back to /login for silent
   * re-auth). Safe no-op when there are no tokens.
   */
  clearLocalSession(): void {
    this.oauth.logOut(true); // clear in-memory tokens only; keep the AS session alive
  }
}
